import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useVaccinations } from '@/hooks/useVaccinations';
import { formatDistanceToNow, isBefore } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Plus } from 'lucide-react';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { useToast } from '@/hooks/use-toast';

export default function VaccinationList() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { vaccinations, isLoading, error, deleteVaccinationRecord } = useVaccinations(petId as string);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingVaccination, setDeletingVaccination] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  const handleDeleteVaccination = async () => {
    if (!deletingVaccination) return;
    
    setIsDeleting(true);
    try {
      await deleteVaccinationRecord(deletingVaccination.id);
      toast({
        title: "Vaccination deleted",
        description: "Vaccination record has been successfully deleted.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete vaccination record. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setDeletingVaccination(null);
    }
  };

  if (isLoading) {
    return <div className="p-4 text-center">Loading vaccinations...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500">Error: {error.message}</p>
        <Button onClick={() => navigate(`/medical/${petId}`)} className="mt-4">
          Back to Medical Records
        </Button>
      </div>
    );
  }

  const getStatusBadge = (dateGiven: string | null, dateDue: string) => {
    if (dateGiven) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
    }
    
    const now = new Date();
    const dueDate = new Date(dateDue);
    
    if (isBefore(dueDate, now)) {
      return <Badge variant="destructive">Overdue</Badge>;
    }
    
    return <Badge variant="secondary">Upcoming</Badge>;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Vaccinations</h1>
        <Button
          onClick={() => navigate(`/medical/${petId}/vaccinations/new`)}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Vaccination
        </Button>
      </div>
      
      <div className="grid gap-4">
        {vaccinations.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground mb-4">No vaccination records found.</p>
              <Button onClick={() => navigate(`/medical/${petId}/vaccinations/new`)}>
                Add First Vaccination Record
              </Button>
            </CardContent>
          </Card>
        ) : (
          vaccinations.map((vaccination) => (
            <Card key={vaccination.id} className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{vaccination.vaccine_name}</h3>
                  {getStatusBadge(vaccination.date_given, vaccination.date_due)}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/medical/${petId}/vaccinations/edit/${vaccination.id}`)}
                    className="flex items-center gap-1"
                  >
                    <Edit className="h-3 w-3" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      setDeletingVaccination({ id: vaccination.id, name: vaccination.vaccine_name });
                      setDeleteDialogOpen(true);
                    }}
                    className="flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm text-muted-foreground">
                {vaccination.date_given ? (
                  <p>Given: {formatDistanceToNow(new Date(vaccination.date_given))} ago</p>
                ) : (
                  <p>Due: {formatDistanceToNow(new Date(vaccination.date_due))} {isBefore(new Date(vaccination.date_due), new Date()) ? 'ago' : 'from now'}</p>
                )}
                
                {vaccination.vet_name && (
                  <p>Vet: {vaccination.vet_name}</p>
                )}
                
                {vaccination.notes && (
                  <p className="text-foreground">{vaccination.notes}</p>
                )}
              </div>
            </Card>
          ))
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteVaccination}
        title="Delete Vaccination Record"
        description="Are you sure you want to delete this vaccination record?"
        itemName={deletingVaccination?.name || ''}
        isLoading={isDeleting}
      />
    </div>
  );
}